import { useCallback, useRef, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import { ColumnDragOverlay } from "@design-system/ViewToolbar/ColumnDragOverlay";
import { useColumnSettingsSensors } from "@design-system/ViewToolbar/useColumnSettingsSensors";
import {
    ColumnItem,
    ColumnItemOverlay,
} from "@design-system/ViewToolbar/ColumnItem";
import { ColumnSettingsTrigger } from "@design-system/ViewToolbar/ColumnSettingsTrigger";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";

export interface SpreadsheetColumnSettingsDef {
    /** Must match the `field` value on the corresponding `ColumnDef`. */
    field: string;
    label: string;
    /**
     * When true the column cannot be hidden and the toggle is disabled.
     * @default false
     */
    immutable?: boolean;
}

interface Props {
    /** All configurable columns, in current display order. */
    columns: readonly SpreadsheetColumnSettingsDef[];
    /** Set of column fields that are currently hidden. */
    hiddenFields: ReadonlySet<string>;
    /**
     * Fields hidden by default (the reset target). The trigger's "modified"
     * dot and the Reset button only show when the current visibility deviates
     * from this baseline, not merely when some column is hidden.
     */
    defaultHiddenFields?: readonly string[];
    /** Called when the user toggles a column's visibility. */
    onToggle: (field: string) => void;
    /**
     * Called when the user clicks "Reset". When omitted the Reset button is
     * not rendered even when columns have been modified.
     */
    onReset?: () => void;
    /**
     * Enables drag-to-reorder on the shown columns. Called with the dragged
     * field and the field it was dropped onto; the parent owns the order.
     */
    onReorder?: (activeField: string, overField: string) => void;
    /** Set when the parent's column order deviates from its default. */
    hasCustomOrder?: boolean;
    /** Trigger button style overrides (defaults: primary, md). */
    triggerVariant?: TriggerButtonProps["variant"];
    triggerSize?: TriggerButtonProps["size"];
}

/**
 * The column gear of a `SpreadsheetGrid`: a settings panel to show and hide
 * its columns and, with `onReorder`, drag the shown ones into order. The
 * parent owns visibility and order, e.g. through
 * `useSpreadsheetColumnVisibility` and `useSpreadsheetColumnOrder`.
 */
export const SpreadsheetColumnSettings = ({
    columns,
    hiddenFields,
    defaultHiddenFields = [],
    onToggle,
    onReset,
    onReorder,
    hasCustomOrder = false,
    triggerVariant = "primary",
    triggerSize = "md",
}: Readonly<Props>) => {
    const scrollableContentRef = useRef<HTMLDivElement>(null);

    const shownColumns = columns.filter(
        (col) => col.immutable || !hiddenFields.has(col.field),
    );
    const hiddenColumns = columns.filter(
        (col) => !col.immutable && hiddenFields.has(col.field),
    );

    const defaultHiddenSet = new Set(defaultHiddenFields);
    const hasModifiedColumns =
        hasCustomOrder ||
        columns.some(
            (col) =>
                !col.immutable &&
                hiddenFields.has(col.field) !== defaultHiddenSet.has(col.field),
        );

    const isReorderable = onReorder !== undefined;
    const [activeField, setActiveField] = useState<string | null>(null);
    const isDragInProgress = activeField !== null;
    const activeColumn = shownColumns.find((col) => col.field === activeField);
    const sensors = useColumnSettingsSensors();

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveField(String(event.active.id));
    }, []);

    const handleDragCancel = useCallback(() => setActiveField(null), []);

    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            setActiveField(null);
            if (!over || active.id === over.id) {
                return;
            }
            onReorder?.(String(active.id), String(over.id));
        },
        [onReorder],
    );

    return (
        <SettingsPanel.Root
            trigger={
                <ColumnSettingsTrigger
                    hasChanges={hasModifiedColumns}
                    variant={triggerVariant}
                    size={triggerSize}
                />
            }
            triggerTooltip={
                isReorderable
                    ? "Change and rearrange visible columns"
                    : "Toggle visible columns"
            }
            footer={
                hasModifiedColumns &&
                onReset && (
                    <SettingsPanel.Action onClick={onReset}>
                        Reset
                    </SettingsPanel.Action>
                )
            }
            scrollContainerRef={scrollableContentRef}
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
                autoScroll={{
                    // Restrict auto-scroll to the popover's scroll container so
                    // drop targets below the fold remain reachable while
                    // dragging, without scrolling the page behind the popover.
                    canScroll: (element) =>
                        element === scrollableContentRef.current,
                }}
            >
                <SettingsPanel.Section label="Shown in table">
                    <SortableContext
                        items={shownColumns.map((col) => col.field)}
                        strategy={verticalListSortingStrategy}
                    >
                        {shownColumns.map((col) => (
                            <ColumnItem
                                key={col.field}
                                id={col.field}
                                name={col.label}
                                isVisible
                                immutable={col.immutable}
                                isDraggable={isReorderable}
                                isDragInProgress={isDragInProgress}
                                onToggleVisibility={() => onToggle(col.field)}
                            />
                        ))}
                    </SortableContext>
                </SettingsPanel.Section>
                <SettingsPanel.Separator />
                <SettingsPanel.Section label="Hidden in table">
                    {hiddenColumns.map((col) => (
                        <ColumnItem
                            key={col.field}
                            id={col.field}
                            name={col.label}
                            isVisible={false}
                            immutable={col.immutable}
                            isDraggable={false}
                            isDragInProgress={isDragInProgress}
                            onToggleVisibility={() => onToggle(col.field)}
                        />
                    ))}
                    {hiddenColumns.length === 0 && (
                        <SettingsPanel.Empty>
                            All columns are visible
                        </SettingsPanel.Empty>
                    )}
                </SettingsPanel.Section>
                <ColumnDragOverlay>
                    {activeColumn && (
                        <ColumnItemOverlay
                            name={activeColumn.label}
                            isLocked={activeColumn.immutable ?? false}
                            hasPinMenu={false}
                        />
                    )}
                </ColumnDragOverlay>
            </DndContext>
        </SettingsPanel.Root>
    );
};
